import * as React from "react";
import { cn } from "@/components/ui/utils";
import { Slot, Tooltip as TooltipPrimitive } from "radix-ui";

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

// Each tooltip its own provider, as shadcn's tooltip was until late 2025: a
// component drawn on its own - in a test, a portal - needs no root above it.
function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

// Not stock: stock marks its trigger "tooltip-trigger" when the trigger is its
// child (asChild) too, writing over the child's own mark - a Button's
// "button", a Badge's "badge" - that styles and keys find it by. Nor does it
// write whether it is open (``data-state``) over the child's own state - a
// toggle's on or off, which it is drawn by: a toggle with a tooltip was drawn
// off however it was pressed.
function TooltipTrigger({
  asChild,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return asChild ? (
    <TooltipPrimitive.Trigger asChild {...props}>
      <ChildsOwnState>{children}</ChildsOwnState>
    </TooltipPrimitive.Trigger>
  ) : (
    <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props}>
      {children}
    </TooltipPrimitive.Trigger>
  );
}

/** The trigger's handlers and ref on its child, and not its ``data-state``. */
function ChildsOwnState({
  "data-state": _state,
  ...props
}: React.ComponentProps<typeof Slot.Root> & { "data-state"?: string }) {
  return <Slot.Root {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  // A tooltip with nothing to say never opens: a call site whose words are
  // only sometimes worth saying (a title cut short) hands it none otherwise.
  if (children === "" || children === null || children === undefined) {
    return null;
  }
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-foreground px-3 py-1.5 text-xs text-balance text-background fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
